/**
 * Condition Step Handler
 * Evaluates conditions and determines next path (true/false branch)
 */

import type {
  StepExecutionContext,
  StepExecutionResult,
  ConditionStepConfig
} from '../../types/workflow';

export async function executeCondition(
  context: StepExecutionContext
): Promise<StepExecutionResult> {
  try {
    const config = context.step.config as ConditionStepConfig;

    if (!config.condition_type) {
      return {
        success: false,
        error: 'Missing required field: condition_type',
      };
    }

    const entityData = context.entityData;
    let conditionMet = false;

    // Evaluate condition based on type
    switch (config.condition_type) {
      case 'field_equals':
        if (config.field_name && entityData[config.field_name] === config.condition_value) {
          conditionMet = true;
        }
        break;

      case 'field_not_equals':
        if (config.field_name && entityData[config.field_name] !== config.condition_value) {
          conditionMet = true;
        }
        break;

      case 'field_contains':
        if (config.field_name && typeof entityData[config.field_name] === 'string') {
          conditionMet = entityData[config.field_name].includes(config.condition_value);
        }
        break;

      case 'field_greater_than':
        if (config.field_name && typeof entityData[config.field_name] === 'number') {
          conditionMet = entityData[config.field_name] > config.condition_value;
        }
        break;

      case 'field_less_than':
        if (config.field_name && typeof entityData[config.field_name] === 'number') {
          conditionMet = entityData[config.field_name] < config.condition_value;
        }
        break;

      case 'field_exists':
        if (config.field_name) {
          conditionMet = entityData[config.field_name] !== null && entityData[config.field_name] !== undefined;
        }
        break;

      case 'field_empty':
        if (config.field_name) {
          const value = entityData[config.field_name];
          conditionMet = value === null || value === undefined || value === '';
        }
        break;

      case 'deal_value_threshold':
        if (entityData.amount || entityData.value) {
          const amount = entityData.amount || entityData.value;
          conditionMet = amount >= config.condition_value;
        }
        break;

      case 'has_tag':
        if (entityData.tags && Array.isArray(entityData.tags)) {
          conditionMet = entityData.tags.includes(config.condition_value);
        }
        break;

      case 'assigned_to_user':
        conditionMet = entityData.assigned_to === config.condition_value;
        break;

      case 'in_pipeline':
        conditionMet = entityData.pipeline_id === config.condition_value;
        break;

      default:
        console.warn(`Unknown condition type: ${config.condition_type}`);
        return {
          success: false,
          error: `Unknown condition type: ${config.condition_type}`,
        };
    }

    console.log(`🔀 Condition "${config.condition_type}" evaluated to: ${conditionMet}`);

    // Determine next step based on condition result
    const nextStepId = conditionMet
      ? context.step.true_next_step_id
      : context.step.false_next_step_id;

    if (!nextStepId) {
      console.log(`⚠️ No next step configured for ${conditionMet ? 'true' : 'false'} branch`);
    }

    return {
      success: true,
      nextStepId: nextStepId || null,
      data: { conditionMet },
    };
  } catch (error) {
    console.error('Error in condition handler:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
